// src/pages/Statistics.jsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import statisticsService from "../../services/statisticsService";
import "./Statistics.css";

function Statistics() {
  const { id: championshipId } = useParams();

  const [stats, setStats] = useState({
    scorers: [],
    yellowCards: [],
    redCards: [],
    goalkeepers: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("scorers");

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);

        const [scorers, cards, goalkeepers] = await Promise.all([
          statisticsService.getScorers(championshipId),
          statisticsService.getCards(championshipId),
          statisticsService.getBestGoalkeepers(championshipId),
        ]);

        const sortedYellowCards = [...cards].sort(
          (a, b) => b.yellowCards - a.yellowCards
        );
        const sortedRedCards = [...cards].sort(
          (a, b) => b.redCards - a.redCards
        );

        setStats({
          scorers,
          yellowCards: sortedYellowCards,
          redCards: sortedRedCards,
          goalkeepers,
        });
      } catch (error) {
        console.error("Erro ao carregar estatísticas:", error);
        setError("Erro ao carregar estatísticas. Tente novamente mais tarde.");
      } finally {
        setLoading(false);
      }
    };

    if (championshipId) {
      fetchStatistics();
    }
  }, [championshipId]);

  const renderPlayerName = (player) => {
    const firstName = player.name ? player.name.split(" ")[0] : "";

    return (
      <span className="player-name">
        {firstName}
        {player.team && <span className="team-name">{player.team.name}</span>}
      </span>
    );
  };

  const renderTable = (type, title, icon, field) => {
    let data = stats[type] || [];

    // Filtra e ordena conforme o tipo
    if (type === "scorers") {
      data = data.filter((p) => p.goals > 0);
    } else if (type === "yellowCards") {
      data = data.filter((p) => p.yellowCards > 0);
    } else if (type === "redCards") {
      data = data.filter((p) => p.redCards > 0);
    } else if (type === "goalkeepers") {
      // ✅ Ordena do menos vazado (menos gols sofridos) para o mais vazado
      data = [...data]
        .filter((p) => p.goalsConceded >= 0)
        .sort((a, b) => a.goalsConceded - b.goalsConceded);
    }

    return (
      <motion.div
        key={type}
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -40 }}
        transition={{ duration: 0.3 }}
        className="stats-section"
      >
        <h3 className="stats-title">
          <span className="icon">{icon}</span> {title}
        </h3>
        <div className="stats-table-container">
          <motion.table
            className="stats-table"
            initial="hidden"
            animate="show"
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.15 } },
            }}
          >
            <thead>
              <tr>
                <th>#</th>
                <th>Jogador</th>
                <th>{field}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((player, index) => (
                <motion.tr
                  key={`${type}-${player._id}`}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0 },
                  }}
                  className={`${index < 3 ? "top-player" : ""} ${
                    index % 2 === 0 ? "even-row" : "odd-row"
                  }`}
                >
                  <td className="rank">
                    {index === 0
                      ? "🥇"
                      : index === 1
                      ? "🥈"
                      : index === 2
                      ? "🥉"
                      : index + 1}
                  </td>
                  <td>{renderPlayerName(player)}</td>
                  <td className="stat-value">
                    {type === "scorers"
                      ? player.goals
                      : type === "yellowCards"
                      ? player.yellowCards
                      : type === "redCards"
                      ? player.redCards
                      : player.goalsConceded}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </motion.table>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <motion.div
          className="loading-spinner"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
        <p>Carregando estatísticas...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="statistics-container">
      <h2 className="statistics-header">Estatísticas do Campeonato</h2>

      {/* Tabs */}
      <div className="tabs-container">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`tab-btn ${activeTab === "scorers" ? "active" : ""}`}
          onClick={() => setActiveTab("scorers")}
        >
          🏆 Artilheiros
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`tab-btn ${activeTab === "yellowCards" ? "active" : ""}`}
          onClick={() => setActiveTab("yellowCards")}
        >
          🟨 Amarelos
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`tab-btn ${activeTab === "redCards" ? "active" : ""}`}
          onClick={() => setActiveTab("redCards")}
        >
          🟥 Vermelhos
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className={`tab-btn ${activeTab === "goalkeepers" ? "active" : ""}`}
          onClick={() => setActiveTab("goalkeepers")}
        >
          🧤 Goleiros
        </motion.button>
      </div>

      <div className="tab-content">
        {activeTab === "scorers" &&
          renderTable("scorers", "Artilheiros", "🏆", "Gols")}
        {activeTab === "yellowCards" &&
          renderTable("yellowCards", "Cartões Amarelos", "🟨", "Amarelos")}
        {activeTab === "redCards" &&
          renderTable("redCards", "Cartões Vermelhos", "🟥", "Vermelhos")}
        {activeTab === "goalkeepers" &&
          renderTable("goalkeepers", "Menos Vazados", "🧤", "Gols Sofridos")}
      </div>
    </div>
  );
}

export default Statistics;